// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
});

const mapDispatchToProps = {
    setUserFields,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupSettingsStyles {
    title?: TypographyPresentationProps;
    fieldset?: InjectableCss;
    wrapper?: InjectableCss;
    userRoleSelect?: SelectPresentationProps;
    approverSelect?: SelectPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // XNG-Changes
    acceptConditions?: CheckboxProps;
    approverSelectLabelWrapper?: InjectableCss;
    roleSelectLabelWrapper?: InjectableCss;
    roleSelectHelpIcon?: IconPresentationProps;
    approverSelectTooltip?: TooltipPresentationProps;
    termsLink?: LinkPresentationProps;
}

export const userSetupSettingsStyles: UserSetupSettingsStyles = {
    title: { variant: "h5" },
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    wrapper: {
        css: css`
            padding-bottom: 15px;
            font-weight: 400;
        `,
    },
};

const styles = userSetupSettingsStyles;
const StyledFieldSet = getStyledWrapper("fieldset");

const UserSetupSettings = ({ editingUser, setUserFields, loadAccount }: Props) => {
    if (!editingUser) {
        return null;
    }

    const [options, setOptions] = useState<OptionObject[]>([]);
    const [isAssignRoleModalOpened, setIsAssignRoleModalOpened] = useState(false);
    const [isTermsModalOpened, setIsTermsModalOpened] = useState(false);

    useEffect(() => {
        // XNG-Changes
        if (editingUser && options.length < 1) {
            setApproverOptions("");
        }

        loadAccount({ accountId: editingUser.id, numberOfApprovers: 20 });
    }, [editingUser]);

    const userRoleChangeHandler: SelectProps["onChange"] = event => {
        setUserFields({ role: event.target.value });
        setUserFields({ approver: "" });
    };

    const approverChangeHandler = (value?: string) => {
        const approverValue = value || "";
        setUserFields({ approver: approverValue });
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filterValue = event.currentTarget.value;
        debouncedSearchApprovers(filterValue);
    };

    const debouncedSearchApprovers = debounce((filterValue: string) => {
        setApproverOptions(filterValue);
    }, 300);

    const setApproverOptions = (filterValue: string) => {
        const currentAccountId = editingUser.id;
        if (!currentAccountId) {
            return;
        }

        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        loadAccount({
            ...parameter,
            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                approverOptions = approverOptions.concat(
                    result.apiResult.availableApprovers.map(o => ({ optionText: o, optionValue: o })),
                );

                setOptions(approverOptions);
            },
        });
    };

    // <XNG-Changes>
    const financialAccessChangeHandler: SelectProps["onChange"] = event => {
        setUserFields({ financialAccess: event.target.value } as any);
    };

    const termsConditionChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setUserFields({ acceptConditions: value } as any);
    };

    const roleSelectLabel = (
        <StyledWrapper {...styles.roleSelectLabelWrapper}>
            {translate("Assign User Role")}
            <Icon
                {...styles.roleSelectHelpIcon}
                onClick={() => setIsAssignRoleModalOpened(true)}
                // XNG-Changes
                aria-label={translate("Open Role Information Modal")}
            />
            <Modal
                headline={translate("User Roles")}
                isOpen={isAssignRoleModalOpened}
                handleClose={() => setIsAssignRoleModalOpened(false)}
            >
                {siteMessage("Custom_Str_Assign_User_Role_Meg")}
            </Modal>
        </StyledWrapper>
    );

    const approverLabel = (
        // XNG-Changes: add styled wrapper
        <StyledWrapper {...styles.approverSelectLabelWrapper}>
            {translate("Assign Approver")}
            <Tooltip
                {...styles.approverSelectTooltip}
                text={siteMessage("Custom_Str_Assign_Approver_Msg") as string}
                // XNG-Changes:fix open dropdown issue
                iconProps={
                    {
                        ...{ onClick: (e: any) => e.preventDefault() },
                        ...styles.approverSelectTooltip?.iconProps,
                    } as any
                }
            />
        </StyledWrapper>
    );
    // </XNG-Changes>

    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupSetting_title">
                {translate("Settings")}
            </Typography>
            <StyledFieldSet {...styles.fieldset}>
                <StyledWrapper {...styles.wrapper}>
                    <Select
                        {...styles.userRoleSelect}
                        label={roleSelectLabel}
                        value={editingUser.role}
                        onChange={userRoleChangeHandler}
                        data-test-selector="userSetupSetting_userRole"
                    >
                        <option value="">{translate("Select User Role")}</option>
                        {editingUser.availableRoles?.map(role => (
                            <option key={role} value={role}>
                                {(editingUser.availableRolesTranslated &&
                                    editingUser.availableRolesTranslated[role.toLowerCase()]) ||
                                    role}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <DynamicDropdown
                        {...styles.approverDynamicDropdown}
                        label={approverLabel}
                        onInputChange={onInputChanged}
                        onSelectionChange={approverChangeHandler}
                        selected={editingUser.approver}
                        options={options}
                        data-test-selector="userSetupSetting_assignApprover"
                        placeholder={translate("Select an Approver")}
                    />
                </StyledWrapper>
                {/* <XNG-Changes> */}
                <StyledWrapper {...styles.wrapper}>
                    <Select
                        label={translate("Financial Access")}
                        value={editingUser.properties.financialAccess}
                        onChange={financialAccessChangeHandler}
                        data-test-selector="userSetupSetting_financialAccess"
                    >
                        <option value="true">{translate("Yes")}</option>
                        <option value="false">{translate("No")}</option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <Checkbox
                        checked={editingUser.properties.acceptConditions as any}
                        onChange={termsConditionChangeHandler}
                        data-test-selector="userSetupSetting_termsCondition"
                        {...styles.acceptConditions}
                    >
                        {siteMessage("STR_Terms_And_Conditions_Label")}
                        <Link
                            {...styles.termsLink}
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsTermsModalOpened(true);
                            }}>
                            {siteMessage("STR_Terms_And_Conditions_Url")}
                        </Link>
                    </Checkbox>
                </StyledWrapper>

                <Modal
                    isOpen={isTermsModalOpened}
                    handleClose={() => {
                        setIsTermsModalOpened(false);
                    }}
                    headline={translate("Terms & conditions")}
                >
                    <Typography>
                        {siteMessage("STR_Terms_And_Conditions_Msg")}
                    </Typography>
                </Modal>
                {/* </XNG-Changes> */}

            </StyledFieldSet>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupSettings),
    definition: {
        group: "User Setup",
        displayName: "Settings",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
